import { z } from "zod";
/**
 * The provider-neutral CAPABILITY vocabulary (Template Strategy V2 §3.3,
 * docs/TEMPLATE-STRATEGY-V2.md). A template declares WHAT the agent needs in
 * these terms; the registry says how each provider implements it (native in
 * the provider sandbox, via a platform substitute, or hosted inside the
 * provider request), and the compilers lower to provider tool names. The
 * legacy builtin-tool names (`BUILTIN_TOOL_NAMES`) remain the compiled
 * Anthropic wire vocabulary and the v2 spec's authoring vocabulary; the maps
 * below are the one translation between the two.
 */
export const CAPABILITY_IDS = [
    "attachments.read",
    "attachments.live",
    "artifact.create",
    "files.edit",
    "shell.exec",
    "web.search",
    "web.fetch",
    "memory.readwrite",
];
export const CapabilityIdSchema = z.enum(CAPABILITY_IDS);
export const IMPLEMENTATION_MODES = ["native", "platform", "hosted"];
export const ImplementationModeSchema = z.enum(IMPLEMENTATION_MODES);
export const REQUIREMENTS = ["required", "optional"];
export const RequirementSchema = z.enum(REQUIREMENTS);
/** Authoring vocabulary for approval; compiled to the frozen always_allow | always_ask wire values. */
export const APPROVALS = ["automatic", "ask"];
export const ApprovalSchema = z.enum(APPROVALS);
export function approvalToPermission(approval) {
    return approval === "ask" ? "always_ask" : "always_allow";
}
export function permissionToApproval(permission) {
    return permission === "always_ask" ? "ask" : "automatic";
}
/**
 * Legacy builtin tool name → capability. `read`/`glob`/`grep` map to null on
 * purpose: resource discovery is a platform guarantee (the Anthropic compiler
 * injects the trio; the loop inlines attachments), never a template concern.
 */
export const BUILTIN_TOOL_CAPABILITY = {
    web_search: "web.search",
    web_fetch: "web.fetch",
    write: "artifact.create",
    edit: "files.edit",
    bash: "shell.exec",
    read: null,
    glob: null,
    grep: null,
};
/** Capability → the Anthropic sandbox builtins that realise it natively. */
export const CAPABILITY_BUILTIN_TOOLS = {
    "web.search": ["web_search"],
    "web.fetch": ["web_fetch"],
    "artifact.create": ["write"],
    "files.edit": ["edit", "write"],
    "shell.exec": ["bash"],
    "attachments.read": ["read", "glob", "grep"],
    "attachments.live": [],
    "memory.readwrite": [],
};
/** Human label per capability (EN/ES) for limitation copy and readiness cards. */
export const CAPABILITY_LABELS = {
    "attachments.read": { en: "reading attached files", es: "leer archivos adjuntos" },
    "attachments.live": {
        en: "attaching files mid-run",
        es: "adjuntar archivos durante la ejecución",
    },
    "artifact.create": { en: "saving deliverable files", es: "guardar archivos entregables" },
    "files.edit": { en: "editing files in place", es: "editar archivos en el lugar" },
    "shell.exec": { en: "running shell commands", es: "ejecutar comandos de shell" },
    "web.search": { en: "web search", es: "búsqueda web" },
    "web.fetch": { en: "fetching web pages", es: "descargar páginas web" },
    "memory.readwrite": { en: "project memory", es: "memoria del proyecto" },
};
/** A limitation the run proceeds under: an optional capability the provider lacks (wire shape; zod 4). */
export const LimitationSchema = z.object({
    capability: z.string().min(1),
    requirement: RequirementSchema,
    reason: z.string().min(1),
    provider: z.enum(["anthropic", "openrouter", "foundry"]),
    detail: z.string().optional(),
});
//# sourceMappingURL=capabilities.js.map